import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, StickyNote, ShoppingBag, CalendarCheck,
  Upload, Landmark, CreditCard, Wallet, ArrowLeftRight,
  Undo2, BookOpen, Settings, LogOut, Menu, X, MessageCircle, CreditCard as CreditCardIcon
} from 'lucide-react';
import { getUser, clearAuth, hasPageAccess } from '../../lib/auth';
import { SeasonalSidebarOverlay, SeasonalLogoDecoration } from './SeasonalEffects';
import api from '../../lib/api';

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'tw1-memo', label: 'Truckwash 1 Memo', icon: StickyNote },
  { key: 'truckshop-memo', label: 'Truckshop 1 Memo', icon: ShoppingBag },
  { key: 'maandafsluiting', label: 'Maandafsluiting', icon: CalendarCheck },
  { key: 'ptw-import', label: 'PTW Fac. Import', icon: Upload },
  { key: 'incassos', label: "Incasso's Exact", icon: Landmark },
  { key: 'betalingen', label: 'Betalingen Exact', icon: CreditCard },
  { key: 'salaris', label: 'Salaris', icon: Wallet },
  { key: 'sepa', label: 'SEPA Betaalbatch', icon: ArrowLeftRight },
  { key: 'storno', label: 'Storno Incasso', icon: Undo2 },
  { key: 'pleo', label: 'Pleo Doorbelasting', icon: CreditCardIcon },
  { key: 'logboek', label: 'Logboek', icon: BookOpen },
  { key: 'teamchat', label: 'Teamchat', icon: MessageCircle },
  { key: 'instellingen', label: 'Instellingen', icon: Settings },
];

export default function Sidebar({ activePage, onNavigate }) {
  const user = getUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [seasonEnabled, setSeasonEnabled] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    api.get('/api/settings/app').then(res => {
      if (res.data.seasonal_themes !== undefined) setSeasonEnabled(res.data.seasonal_themes);
    }).catch(() => {});
    // Fetch unread chat count
    api.get('/api/chat/unread-count').then(res => setUnreadCount(res.data.count || 0)).catch(() => {});
    const iv = setInterval(() => {
      api.get('/api/chat/unread-count').then(res => setUnreadCount(res.data.count || 0)).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  const visibleItems = NAV_ITEMS.filter(item => hasPageAccess(item.key));

  const handleLogout = () => {
    clearAuth();
    window.location.reload();
  };

  const handleNavigate = (key) => {
    onNavigate(key);
    setMobileOpen(false);
    if (key === 'teamchat') {
      api.post('/api/chat/mark-read').then(() => setUnreadCount(0)).catch(() => {});
    }
  };

  const renderNav = (prefix) => (
    <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const isActive = activePage === item.key;
        const showBadge = item.key === 'teamchat' && unreadCount > 0 && !isActive;
        return (
          <button
            key={item.key}
            data-testid={`${prefix}-nav-${item.key}`}
            onClick={() => handleNavigate(item.key)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors duration-150 relative group ${
              isActive
                ? 'bg-white/10 text-[#f5a800]'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            }`}
          >
            {isActive && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#f5a800] rounded-r-full" />
            )}
            <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-[#f5a800]' : 'text-white/50 group-hover:text-white/70'}`} />
            <span className="truncate">{item.label}</span>
            {showBadge && (
              <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1" data-testid="chat-unread-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );

  const renderProfile = () => (
    <div className="px-4 py-4 border-t border-white/10">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#f5a800]/20 flex items-center justify-center">
          <span className="text-[#f5a800] text-sm font-bold">{user?.naam?.[0] || 'U'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{user?.naam || 'Gebruiker'}</p>
          <p className="text-white/40 text-xs truncate">{user?.rol || 'gebruiker'}</p>
        </div>
      </div>
      <button
        data-testid="sidebar-logout"
        onClick={handleLogout}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 text-sm transition-colors"
      >
        <LogOut className="w-4 h-4" />
        <span>Uitloggen</span>
      </button>
    </div>
  );

  const renderLogo = (showClose) => (
    <div className="px-5 pt-6 pb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src="/tw1-logo.png" alt="TW1 Group" className="w-10 h-10 rounded-xl object-contain" />
            <SeasonalLogoDecoration enabled={seasonEnabled} />
          </div>
          <div>
            <h2 className="text-white font-bold text-base tracking-tight">TW1 Group</h2>
            <p className="text-white/40 text-xs">Administratie</p>
          </div>
        </div>
        {showClose && (
          <button className="text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        data-testid="sidebar-mobile-toggle"
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-xl bg-[#003366] flex items-center justify-center shadow-lg"
      >
        <Menu className="w-5 h-5 text-white" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile sidebar */}
      <aside className={`lg:hidden !fixed inset-y-0 left-0 z-50 tw1-sidebar w-[272px] min-h-screen flex flex-col transform transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {renderLogo(true)}
        {renderNav('mobile')}
        {renderProfile()}
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex tw1-sidebar w-[272px] min-h-screen flex-col relative z-10">
        <SeasonalSidebarOverlay enabled={seasonEnabled} />
        {renderLogo(false)}
        {renderNav('sidebar')}
        {renderProfile()}
      </aside>
    </>
  );
}
